import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    {
      name: 'Event',
      type: 'group',
      children: [
        { name: 'Daftar Event', path: '/event', activePaths: ['/event'] },
        { name: 'Jadwal & Hasil', path: '/jadwal-hasil', activePaths: ['/jadwal-hasil'] },
        { name: 'Klasemen', path: '/klasemen', activePaths: ['/klasemen'] },
        { name: 'Statistik', path: '/statistik', activePaths: ['/statistik'] },
      ],
    },
    {
      name: 'Peserta',
      type: 'group',
      children: [
        { name: 'Sekolah', path: '/peserta-sekolah', activePaths: ['/peserta-sekolah'] },
        { name: 'Klub', path: '/peserta-klub', activePaths: ['/peserta-klub'] },
      ],
    },
    { name: 'Berita', type: 'link', path: '/berita', activePaths: ['/berita'] },
    { name: 'Galeri', type: 'link', path: '/galeri', activePaths: ['/galeri'] },
    { name: 'Tentang Kami', type: 'link', path: '/tentang-kami', activePaths: ['/tentang-kami'] },
  ];

  const isItemActive = (item) => {
    const activePaths = item.activePaths || [];
    const pathMatched = activePaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
    if (!pathMatched) return false;

    if (!item.activeQuery) return true;

    const params = new URLSearchParams(location.search);
    return Object.entries(item.activeQuery).every(([key, value]) => params.get(key) === value);
  };

  const isGroupActive = (group) => group.children?.some((item) => isItemActive(item));

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setOpenMobileGroup(null);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="bg-gradient-to-r from-white via-emerald-50 to-white border-b border-emerald-100 shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-3 cursor-pointer" aria-label="Go to home">
            <img
              src="https://customer-assets.emergentagent.com/job_90560e06-7136-451f-8b9f-e5b1a310f4fa/artifacts/3z4o6h4i_2133.png"
              alt="IJSL Logo"
              className="h-16 w-auto"
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/220x80?text=IJSL';
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              if (item.type === 'group') {
                const groupActive = isGroupActive(item);
                return (
                  <div key={item.name} className="relative group">
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 relative ${
                        groupActive
                          ? 'bg-green-600 text-white'
                          : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="h-4 w-4" />
                      {groupActive && <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-emerald-200 rounded-full" />}
                    </button>
                    <div className="invisible opacity-0 -translate-y-1 scale-[0.98] group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100 transition-all duration-300 ease-out origin-top absolute left-0 top-full mt-2 w-64 rounded-xl border border-emerald-100 bg-white/95 backdrop-blur-sm shadow-xl p-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                            isItemActive(child)
                              ? 'bg-emerald-600 text-white'
                              : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          <span>{child.name}</span>
                          <span className={`h-2 w-2 rounded-full ${isItemActive(child) ? 'bg-white/90' : 'bg-transparent'}`} />
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 relative ${
                    isItemActive(item)
                      ? 'bg-green-600 text-white'
                      : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                  }`}
                >
                  {item.name}
                  {isItemActive(item) && <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-emerald-200 rounded-full" />}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-emerald-100 space-y-1">
            {navItems.map((item) => {
              if (item.type === 'group') {
                const expanded = openMobileGroup === item.name;
                return (
                  <div key={item.name} className="rounded-lg border border-emerald-100 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenMobileGroup(expanded ? null : item.name)}
                      className={`w-full px-4 py-3 text-sm font-semibold flex items-center justify-between ${
                        isGroupActive(item) ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded && (
                      <div className="bg-emerald-50/70">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setOpenMobileGroup(null);
                            }}
                            className={`block px-4 py-2 text-sm ${
                              isItemActive(child)
                                ? 'bg-emerald-600 text-white'
                                : 'text-gray-700 hover:bg-emerald-100'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setIsMenuOpen(false);
                    setOpenMobileGroup(null);
                  }}
                  className={`block px-4 py-3 text-sm font-semibold rounded-lg transition-colors ${
                    isItemActive(item)
                      ? 'bg-green-600 text-white'
                      : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;